import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine, Eyebrow } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG = "/hero-main.jpg";

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 200]);
  const scale = useTransform(scrollY, [0, 900], [1, 1.12]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={HERO_IMG}
          alt="Kitchen and dining space refurbished by Preece Refurbishments"
          className="w-full h-full object-cover img-treated"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-[#121212]" />
      </motion.div>

      <div className="relative z-10 max-w-[1440px] w-full mx-auto px-6 md:px-12 pt-40 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-12 copper-line" />
          <Eyebrow>Family owned · Property refurbishment</Eyebrow>
        </motion.div>

        <h1 className="font-serif text-[13vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem] leading-[1.02] tracking-tight text-[#f5f5f0]">
          <MaskedLine delay={0.35}>Helping achieve</MaskedLine>
          <MaskedLine delay={0.5}>the space</MaskedLine>
          <MaskedLine delay={0.65}>
            <span>
              you{" "}
              <em className="italic text-[#c5832b]">love.</em>
            </span>
          </MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="max-w-md text-base md:text-lg text-[#a3a3a3] leading-relaxed"
          >
            From single rooms to whole homes — Preece Refurbishments brings
            family-run care and builder-grade craft to every project.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-quote-cta"
              onClick={() => scrollToId("quote")}
              className="group flex items-center gap-3 bg-[#c5832b] hover:bg-[#d9953c] text-[#121212] font-semibold px-8 py-4 rounded-[2px] transition-colors duration-300"
            >
              Request a quote
              <ArrowUpRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </button>
            <button
              data-testid="hero-work-cta"
              onClick={() => scrollToId("projects")}
              className="px-8 py-4 border border-white/25 hover:border-[#c5832b] hover:text-[#c5832b] text-[#f5f5f0] font-medium rounded-[2px] transition-colors duration-300"
            >
              See our work
            </button>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 hidden md:flex items-center gap-2 text-[#a3a3a3] hover:text-[#c5832b] transition-colors duration-300"
        aria-label="Scroll to services"
      >
        <span className="text-xs uppercase tracking-[0.25em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
